import { useState, useRef } from 'react';

interface RecognitionResult {
  label: string;
  confidence: number;
  description: string;
}

export default function ImageRecognitionPage() {
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [results, setResults] = useState<RecognitionResult[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = [
    { id: 'all', name: '全部', icon: 'ri-grid-line' },
    { id: 'animal', name: '动物', icon: 'ri-bear-smile-line' },
    { id: 'plant', name: '植物', icon: 'ri-plant-line' },
    { id: 'food', name: '食物', icon: 'ri-cake-3-line' },
    { id: 'object', name: '物品', icon: 'ri-box-3-line' },
    { id: 'vehicle', name: '交通工具', icon: 'ri-car-line' }
  ];

  const sampleImages = [
    { url: 'https://public.readdy.ai/ai/img_res/cff65f2aba1c5114ac19a5b5d67ca606.jpg', category: 'animal', name: '小狗' },
    { url: 'https://readdy.ai/api/search-image?query=beautiful%20red%20rose%20flower%20with%20green%20leaves%2C%20close%20up%20photography%2C%20natural%20lighting%2C%20detailed%20petals&width=400&height=400&seq=1002&orientation=squarish', category: 'plant', name: '玫瑰花' },
    { url: 'https://readdy.ai/api/search-image?query=delicious%20chocolate%20cake%20with%20strawberries%20on%20top%2C%20white%20plate%2C%20professional%20food%20photography%2C%20detailed%20texture&width=400&height=400&seq=1003&orientation=squarish', category: 'food', name: '蛋糕' },
    { url: 'https://readdy.ai/api/search-image?query=orange%20tabby%20cat%20sitting%20on%20windowsill%2C%20cute%20expression%2C%20detailed%20fur%2C%20natural%20lighting&width=400&height=400&seq=1004&orientation=squarish', category: 'animal', name: '小猫' },
    { url: 'https://readdy.ai/api/search-image?query=green%20succulent%20plant%20in%20white%20ceramic%20pot%2C%20minimalist%20style%2C%20clean%20background%2C%20detailed%20leaves&width=400&height=400&seq=1005&orientation=squarish', category: 'plant', name: '多肉植物' },
    { url: 'https://readdy.ai/api/search-image?query=red%20sports%20car%20on%20road%2C%20modern%20design%2C%20shiny%20paint%2C%20professional%20automotive%20photography&width=400&height=400&seq=1006&orientation=squarish', category: 'vehicle', name: '汽车' }
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target?.result as string);
        setResults([]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSampleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setResults([]);
  };

  const recognizeImage = () => {
    if (!selectedImage) return;

    setIsRecognizing(true);
    setResults([]);

    // 模拟AI识别过程
    setTimeout(() => {
      const mockResults: RecognitionResult[] = [
        {
          label: '金毛犬',
          confidence: 98.5,
          description: '金毛寻回犬是一种大型犬种，性格温顺友好，是很受欢迎的家庭宠物。'
        },
        {
          label: '狗',
          confidence: 99.2,
          description: '狗是人类最忠诚的朋友，已经陪伴人类超过一万年了。'
        },
        {
          label: '宠物',
          confidence: 95.8,
          description: '宠物是人类饲养的动物伙伴，能给我们带来快乐和陪伴。'
        }
      ];

      setResults(mockResults);
      setIsRecognizing(false);
    }, 2000);
  };

  const filteredSamples = selectedCategory === 'all' 
    ? sampleImages 
    : sampleImages.filter(img => img.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/primary')}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-700"></i>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <i className="ri-camera-line text-2xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">图像识别小能手</h1>
                  <p className="text-sm text-gray-600">让AI帮你认识世界万物</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Image Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upload Area */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">选择图片</h2>
              
              <div className="mb-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-6 py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-500 transition-all cursor-pointer bg-gray-50 hover:bg-green-50"
                >
                  <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                  <p className="text-gray-600 font-semibold">点击上传图片</p>
                  <p className="text-sm text-gray-400 mt-1">支持 JPG、PNG 格式</p>
                </button>
              </div>

              {/* Category Filter */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-700 mb-3">或选择示例图片：</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all cursor-pointer ${
                        selectedCategory === cat.id
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <i className={`${cat.icon} mr-2`}></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sample Images */}
              <div className="grid grid-cols-3 gap-4">
                {filteredSamples.map((sample, index) => (
                  <div
                    key={index}
                    onClick={() => handleSampleImageClick(sample.url)}
                    className="relative group cursor-pointer rounded-xl overflow-hidden border-2 border-gray-200 hover:border-green-500 transition-all"
                  >
                    <img
                      src={sample.url}
                      alt={sample.name}
                      className="w-full h-32 object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                      <span className="text-white font-semibold">{sample.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Image Preview */}
            {selectedImage && (
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">预览图片</h2>
                <div className="relative">
                  <img
                    src={selectedImage}
                    alt="Selected"
                    className="w-full rounded-xl"
                  />
                </div>
                <button
                  onClick={recognizeImage}
                  disabled={isRecognizing}
                  className="w-full mt-4 px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRecognizing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      AI正在识别中...
                    </>
                  ) : (
                    <>
                      <i className="ri-eye-line mr-2"></i>
                      开始识别
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-search-eye-line mr-2 text-green-600"></i>
                识别结果
              </h2>

              {results.length > 0 ? (
                <div className="space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="border-2 border-gray-100 rounded-xl p-4 hover:border-green-300 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-lg text-gray-900">{result.label}</h3>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          {result.confidence}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div
                          className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${result.confidence}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600">{result.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <i className="ri-search-line text-6xl mb-4"></i>
                  <p>选择图片后点击识别</p>
                  <p className="text-sm mt-2">AI将告诉你这是什么</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <i className="ri-lightbulb-line text-blue-600 text-2xl mt-1"></i>
            <div>
              <h3 className="font-bold text-blue-900 mb-2">使用小贴士：</h3>
              <ul className="space-y-1 text-blue-800 text-sm">
                <li>• 可以上传自己拍摄的照片，或选择示例图片</li>
                <li>• 图片越清晰，识别结果越准确</li>
                <li>• AI会告诉你物体的名称和相关知识</li>
                <li>• 试试识别不同的动物、植物和物品吧！</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
